import type { ResolvedContext } from './resolved-context.types'

/** A bare classification ref as returned by the live `matching`/`search`. */
interface ObjectRefLike {
	objectType?: string
	objectId: string
}

/**
 * The live `GET /work-orders/{id}/context` returns THIN refs — `files` and
 * `references` as `{ objectId }[]` (no labels; brand-kit + identity hydration have no
 * backend yet, see resolved-context.types). The MSW mock returns the RICH
 * `ResolvedContext` for the demo. `normalizeContext` accepts either and always yields
 * the panel's shape:
 *  - rich shape → passthrough;
 *  - thin shape → references mapped to minimal display rows (id only), brand-kit +
 *    identity left empty until their services land.
 *
 * The endpoint's `rules` refs are ignored on both paths. Rendering them meant inventing
 * a statement, a type and a severity per ref; the applicable-rules section resolves the
 * order's rules hydrated instead.
 */
export function normalizeContext(raw: unknown): ResolvedContext {
	const c = (raw ?? {}) as Record<string, unknown>

	// Already the rich shape (mock / future enriched DTO) — trust it.
	if (Array.isArray(c.brandKit) || Array.isArray(c.identity)) {
		return {
			brandKit: (c.brandKit as ResolvedContext['brandKit']) ?? [],
			identity: (c.identity as ResolvedContext['identity']) ?? [],
			deliverables: (c.deliverables as ResolvedContext['deliverables']) ?? [],
			editableSources: (c.editableSources as ResolvedContext['editableSources']) ?? [],
		}
	}

	// Thin live shape: bare refs. Map what we can; leave the rest empty.
	const refs = (v: unknown): ObjectRefLike[] => (Array.isArray(v) ? (v as ObjectRefLike[]) : [])
	return {
		brandKit: [],
		identity: [],
		// The live `references` (tuple matches) become the reference-deliverables list.
		deliverables: refs(c.references).map((f) => ({ id: f.objectId, title: f.objectId, channel: '—', ratio: '—' })),
		// The live `editableSources` bare refs become the editable-source-files list.
		editableSources: refs(c.editableSources).map((f) => ({
			id: f.objectId,
			title: f.objectId,
			channel: '—',
			ratio: '—',
		})),
	}
}

/**
 * Whether a resolved context has anything the panel shows. `deliverables` does not
 * count: the panel has no box for them, so they alone leave it with nothing in it.
 */
export function hasResolvedContextContent(context: ResolvedContext): boolean {
	const shown = [context.brandKit, context.identity, context.editableSources]
	return shown.some((items) => (items ?? []).length > 0)
}
